/**
 * MemberCard Component
 * Displays individual organization member with quota usage
 */
import { MemberWithQuota } from '../../api/organizations';
import Badge from '../common/Badge';
import ProgressBar from '../common/ProgressBar';

interface MemberCardProps {
  member: MemberWithQuota;
  isCurrentUser: boolean;
  isOwner: boolean;
  isAdmin: boolean;
  onEditQuota?: (member: MemberWithQuota) => void;
  onRemove?: (member: MemberWithQuota) => void;
}

export default function MemberCard({
  member,
  isCurrentUser,
  isOwner,
  isAdmin,
  onEditQuota,
  onRemove,
}: MemberCardProps) {
  const { member: memberInfo, quota } = member;

  // Calculate last active display
  const getLastActiveText = (lastLogin: string | undefined) => {
    if (!lastLogin) return 'Never logged in';

    const lastLoginDate = new Date(lastLogin);
    const now = new Date();
    const diffMs = now.getTime() - lastLoginDate.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Active now';
    if (diffHours < 24) return `Active ${diffHours}h ago`;
    if (diffDays === 1) return 'Active 1 day ago';
    return `Active ${diffDays} days ago`;
  };

  // Format join date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Determine card styling based on role
  const cardBgClass = isAdmin || isOwner
    ? 'bg-accent-blue/5 border-accent-blue/30'
    : 'bg-dark-card/50 border-dark-border/50';

  return (
    <div className={`border rounded-lg p-5 space-y-4 transition-all ${cardBgClass}`}>
      {/* Header with name and badges */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {/* Avatar placeholder */}
            <div className="w-10 h-10 rounded-full bg-dark-bg/50 flex items-center justify-center text-dark-text font-semibold">
              {memberInfo.full_name
                ? memberInfo.full_name.charAt(0).toUpperCase()
                : memberInfo.email.charAt(0).toUpperCase()}
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-dark-text font-semibold">
                  {memberInfo.full_name || memberInfo.preferred_name || 'Unnamed User'}
                  {isCurrentUser && ' (You)'}
                </span>

                {isOwner && <Badge variant="success" size="sm">OWNER</Badge>}
                {isAdmin && <Badge variant="primary" size="sm">ADMIN</Badge>}
                {!memberInfo.is_active && <Badge variant="danger" size="sm">INACTIVE</Badge>}
              </div>

              <div className="text-sm text-dark-text-muted">
                {memberInfo.email}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Metadata row */}
      <div className="text-xs text-dark-text-muted flex items-center gap-3">
        <span>Joined {formatDate(memberInfo.created_at)}</span>
        <span>•</span>
        <span>{getLastActiveText(memberInfo.last_login)}</span>
      </div>

      {/* Quota displays */}
      {quota ? (
        <div className="space-y-3">
          <ProgressBar
            current={quota.used_clipboard_slots}
            max={quota.allocated_clipboard_slots}
            label="Clipboard Slots"
            height="sm"
          />

          <ProgressBar
            current={quota.used_conversations}
            max={quota.allocated_conversations}
            label="Conversations"
            height="sm"
          />

          <ProgressBar
            current={quota.used_ai_requests}
            max={quota.allocated_ai_requests}
            label="AI Requests (monthly)"
            height="sm"
          />
        </div>
      ) : (
        <div className="bg-dark-bg/30 border border-dark-border/50 rounded-lg p-4">
          <p className="text-sm text-dark-text-muted">
            {isAdmin || isOwner
              ? 'Organization admin - Full access to all features'
              : 'No quota allocation'}
          </p>
        </div>
      )}

      {/* Action buttons (only for non-admin, non-owner members) */}
      {!isAdmin && !isOwner && onEditQuota && onRemove && (
        <div className="flex gap-2 pt-2 border-t border-dark-border/50">
          <button
            onClick={() => onEditQuota(member)}
            className="flex-1 px-4 py-2 text-sm text-white bg-dark-bg/50 border border-dark-border
                     rounded-lg hover:bg-dark-bg hover:border-accent-blue
                     transition-colors"
          >
            Edit Quota
          </button>

          <button
            onClick={() => onRemove(member)}
            className="flex-1 px-4 py-2 text-sm text-white bg-dark-bg/50 border border-dark-border
                     rounded-lg hover:bg-red-500/20 hover:border-red-500/50
                     transition-colors"
          >
            Remove Member
          </button>
        </div>
      )}
    </div>
  );
}
